import type { DashboardLayout, WidgetSize, WidgetType } from '@sea-snag/shared';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Calendar,
  CalendarRange,
  CheckCircle,
  Ship,
  Sun,
  Target,
  Waves,
} from 'lucide-react';
import React from 'react';

// ── Widget Props ─────────────────────────────────────────────────────

export interface WidgetProps {
  widgetId: string;
  config: Record<string, unknown>;
}

// ── Config Field Definitions ─────────────────────────────────────────

export interface ConfigFieldOption {
  label: string;
  value: string;
}

export interface ConfigField {
  key: string;
  label: string;
  type: 'year' | 'boat';
}

// ── Widget Category ──────────────────────────────────────────────────

export type WidgetCategory = 'stat' | 'chart' | 'list' | 'analysis' | 'operational';

export interface WidgetCategoryInfo {
  id: WidgetCategory;
  label: string;
  description: string;
}

export const WIDGET_CATEGORIES: WidgetCategoryInfo[] = [
  { id: 'stat', label: 'Statistics', description: 'Key performance indicators at a glance' },
  { id: 'chart', label: 'Charts', description: 'Visual trends and comparisons' },
  { id: 'list', label: 'Lists', description: 'Activity feeds and data lists' },
  { id: 'analysis', label: 'Analysis', description: 'Market intelligence and insights' },
  { id: 'operational', label: 'Operational', description: 'Setup and operational tools' },
];

// ── Widget Registration ──────────────────────────────────────────────

export interface WidgetRegistration {
  type: WidgetType;
  name: string;
  description: string;
  category: WidgetCategory;
  size: WidgetSize;
  icon: LucideIcon;
  component: React.LazyExoticComponent<React.ComponentType<WidgetProps>>;
  configFields: ConfigField[];
  defaultConfig: Record<string, unknown>;
  emptyStateMessage: string;
  /**
   * Hidden from members.
   *
   * Set on every widget that reads an endpoint a member is refused — not just
   * `/analytics/*`. `getting-started` is the one that proved the narrower rule
   * wrong: it calls integrations and members, so for a member both queries
   * failed, its checklist never completed, and the card never auto-dismissed —
   * leaving them staring at steps they cannot perform, linking to screens that
   * bounce them back.
   */
  adminOnly?: boolean;
}

// ── Registry ─────────────────────────────────────────────────────────

export const WIDGET_REGISTRY: Record<WidgetType, WidgetRegistration> = {
  'occupancy-by-month-chart': {
    adminOnly: true,
    type: 'occupancy-by-month-chart',
    name: 'Occupancy by Month',
    description: 'Monthly occupancy rates across your fleet',
    category: 'chart',
    size: 'half',
    icon: Calendar,
    component: React.lazy(() => import('./widgets/chart/occupancy-by-month')),
    // Year comes from the header control, not from widget config.
    configFields: [],
    defaultConfig: {},
    emptyStateMessage: 'No occupancy data to chart yet',
  },
  'occupancy-by-week-chart': {
    adminOnly: true,
    type: 'occupancy-by-week-chart',
    name: 'Occupancy by Week',
    description: 'Weekly fleet occupancy across the charter year',
    category: 'chart',
    size: 'half',
    icon: CalendarRange,
    component: React.lazy(() => import('./widgets/chart/occupancy-by-week')),
    // Year comes from the header control, not from widget config.
    configFields: [],
    defaultConfig: {},
    emptyStateMessage: 'No occupancy data to chart yet',
  },
  'market-this-week': {
    type: 'market-this-week',
    name: 'Market This Week',
    description: "This week's market demand vs your fleet, from your competitor sets",
    category: 'analysis',
    size: 'half',
    icon: Waves,
    component: React.lazy(() => import('./widgets/analysis/market-this-week')),
    configFields: [],
    defaultConfig: {},
    emptyStateMessage: 'Set up a competitor set to see market demand',
  },
  'revenue-vs-target': {
    adminOnly: true,
    type: 'revenue-vs-target',
    name: 'Revenue vs Target',
    description: 'Season revenue against your fleet-wide targets',
    category: 'stat',
    size: 'half',
    icon: Target,
    component: React.lazy(() => import('./widgets/stat/revenue-vs-target')),
    // Year comes from the header control, not from widget config.
    configFields: [],
    defaultConfig: {},
    emptyStateMessage: 'No revenue data yet, connect your charter integration.',
  },
  'revenue-by-boat-chart': {
    adminOnly: true,
    type: 'revenue-by-boat-chart',
    name: 'Revenue at Risk',
    description: 'Boats behind expected pace against their season targets',
    category: 'chart',
    size: 'half',
    icon: Ship,
    component: React.lazy(() => import('./widgets/chart/revenue-by-boat')),
    // Year comes from the header control, not from widget config.
    configFields: [],
    defaultConfig: {},
    emptyStateMessage: 'No fleet data yet, connect your charter integration.',
  },
  'activity-feed': {
    type: 'activity-feed',
    name: 'Activity Feed',
    description: 'Recent actions and events in your organization',
    category: 'list',
    size: 'half',
    icon: Activity,
    component: React.lazy(() => import('./widgets/list/activity-feed')),
    configFields: [],
    defaultConfig: {},
    emptyStateMessage: 'No recent activity',
  },
  'season-progress': {
    type: 'season-progress',
    name: 'Season Progress',
    description: 'Charter season booking progress and projections',
    category: 'analysis',
    size: 'full',
    icon: Sun,
    component: React.lazy(() => import('./widgets/analysis/season-progress')),
    configFields: [],
    defaultConfig: {},
    emptyStateMessage: 'No season data available yet',
  },
  'getting-started': {
    adminOnly: true,
    type: 'getting-started',
    name: 'Getting Started',
    description: 'Setup checklist to get your account fully configured',
    category: 'operational',
    size: 'full',
    icon: CheckCircle,
    component: React.lazy(() => import('./widgets/operational/getting-started')),
    configFields: [],
    defaultConfig: {},
    emptyStateMessage: 'Complete setup to unlock all features',
  },
};

// ── Default Layout ───────────────────────────────────────────────────

const currentYear = new Date().getFullYear().toString();

export const DEFAULT_LAYOUT: DashboardLayout = {
  widgets: [
    // Getting Started self-dismisses once setup is complete (see the widget)
    { id: crypto.randomUUID(), type: 'getting-started', position: 0, config: {} },
    // Season progress (full width)
    { id: crypto.randomUUID(), type: 'season-progress', position: 1, config: {} },
    // Row: occupancy by week | occupancy by month
    {
      id: crypto.randomUUID(),
      type: 'occupancy-by-week-chart',
      position: 2,
      config: { year: currentYear },
    },
    {
      id: crypto.randomUUID(),
      type: 'occupancy-by-month-chart',
      position: 3,
      config: { year: currentYear },
    },
    // Row: revenue at risk | market this week
    {
      id: crypto.randomUUID(),
      type: 'revenue-by-boat-chart',
      position: 4,
      config: { year: currentYear },
    },
    { id: crypto.randomUUID(), type: 'market-this-week', position: 5, config: {} },
    // Row: activity feed | revenue vs target
    { id: crypto.randomUUID(), type: 'activity-feed', position: 6, config: {} },
    {
      id: crypto.randomUUID(),
      type: 'revenue-vs-target',
      position: 7,
      config: { year: currentYear },
    },
  ],
};

/**
 * The widgets a given viewer may add, and may keep on their dashboard.
 *
 * Both matter: `DEFAULT_LAYOUT` already contains the revenue widgets for every
 * user created so far, so filtering only the catalog would leave them sitting
 * on existing members' dashboards, failing every refresh.
 */
export function visibleWidgets(canSeeAdmin: boolean): WidgetRegistration[] {
  const all = Object.values(WIDGET_REGISTRY);
  return canSeeAdmin ? all : all.filter((w) => !w.adminOnly);
}
